// BUILD-046 Owner-requested polish (visual edits ledger, session edit 4.3): the "Show requester" display preference of the Compact view.
//
// A checkbox of the Filter popover's "Display" section (it was an item of the View menu before increment 1b-ii), offered only while Compact is selected. OFF by default (Compact is one tight line with no requester). When ON, Compact draws the
// short requester badge ("By Julie · Oct 18") in its OWN aligned "Requested" column, right after the Task column, on My Tasks, Shared Tasks, Team and Company;
// Comfortable always shows the badge below the title and is not affected by this preference.
//
// Kept exactly like the density preference (densityStore.ts): ONE preference for the whole My Work page, so it survives tab changes, and a reload (localStorage key
// 'build046.showRequester', stored as 'true' / 'false'). It is a display preference of the pilot, not tied to the Caller. A storage that is missing or refuses to
// write (a private window) leaves the preference in memory for the life of the page. The module-level `showRequester` store is what the views read
// (useSyncExternalStore(showRequester.subscribe, showRequester.get)); `createShowRequesterStore` takes the storage so the tests use a fake one.
//
// Erasable TypeScript only; type-only import.

import type { StorageLike } from './densityStore';

export const SHOW_REQUESTER_STORAGE_KEY = 'build046.showRequester';

export interface ShowRequesterStore {
  get: () => boolean;
  set: (next: boolean) => void;
  /** Switches the preference; returns the new value. */
  toggle: () => boolean;
  subscribe: (listener: () => void) => () => void;
}

function safeGet(storage: StorageLike | null, key: string): string | null {
  if (storage === null) return null;
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

export function createShowRequesterStore(storage: StorageLike | null, key: string = SHOW_REQUESTER_STORAGE_KEY, initial: boolean = false): ShowRequesterStore {
  const stored = safeGet(storage, key);
  let value = stored === null ? initial : stored === 'true';
  const listeners = new Set<() => void>();
  function set(next: boolean): void {
    if (typeof next !== 'boolean' || next === value) return;
    value = next;
    if (storage !== null) {
      try {
        storage.setItem(key, next ? 'true' : 'false');
      } catch {
        // the choice still holds for this page
      }
    }
    for (const listener of [...listeners]) listener();
  }
  return {
    get: () => value,
    set,
    toggle() {
      set(!value);
      return value;
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

function browserStorage(): StorageLike | null {
  try {
    const candidate = (globalThis as { localStorage?: StorageLike }).localStorage;
    return candidate ?? null;
  } catch {
    return null;
  }
}

/** The page's "Show requester" preference (Compact: the Requested column; OFF by default). */
export const showRequester: ShowRequesterStore = createShowRequesterStore(browserStorage());

/**
 * Owner correction 2026-10-24 (item 4): in Comfortable the requester is visible BY DEFAULT ("By Julie - Oct 18" under the title), and the Display menu can hide it. A separate preference from the
 * Compact column's, ON by default; kept like it (localStorage 'build046.showRequesterComfortable'). Assigned by Me never repeats the requester (it is the viewer).
 */
export const SHOW_REQUESTER_COMFORTABLE_KEY = 'build046.showRequesterComfortable';
export const showRequesterComfortable: ShowRequesterStore = createShowRequesterStore(browserStorage(), SHOW_REQUESTER_COMFORTABLE_KEY, true);

/** Whether a Comfortable row draws the requester badge: when the preference is on (Compact has its own column and is not affected). */
export function comfortableRequesterShown(density: unknown, show: boolean): boolean {
  return density === 'compact' || show === true;
}

/**
 * Whether the table has the "Requested" column: only in Compact, only when "Show requester" is on, and never in Assigned by Me (variant 'issued'), which has no
 * requester. Comfortable shows the badge below the title instead.
 */
export function requestedColumnShown(density: unknown, show: boolean, variant: string = 'mine'): boolean {
  return density === 'compact' && show === true && variant !== 'issued';
}
